import { useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, Send } from "lucide-react";

const SERVICES = ["Website Design", "Video Ad Creation", "Both services"];

export const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", email: "", service: "", budget: "", details: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const getAvailablePlans = (selectedService) => {
    if (selectedService === "Website Design") return ["$1299"];
    if (selectedService === "Video Ad Creation") return ["$799"];
    if (selectedService === "Both services") return ["$2599", "$5000"];
    return ["$799", "$1299", "$2599", "$5000"];
  };

  const availablePlans = getAvailablePlans(form.service);

  const set = (k) => (e) => {
    const value = e.target.value;
    if (k === "service") {
      setForm({ ...form, service: value, budget: "" });
    } else {
      setForm({ ...form, [k]: value });
    }
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.service) e.service = "Please choose a service";
    if (!form.budget) e.budget = "Please select a plan";
    if (!form.details.trim()) e.details = "Tell us a little about your project";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) return setErrors(e);
    setStatus("loading");
    
    try {
      
      await axios.post("https://api.web3forms.com/submit", {
        access_key: "0249d560-d0b7-4752-83a4-c24782f0cb0d", 
        subject: `New Lead from ${form.name} - BHF Creative`,
        from_name: "BHF Creative Website",
        Name: form.name,
        Email: form.email,
        Service_Needed: form.service,
        Selected_Plan: form.budget,
        Project_Details: form.details,
      });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="quote-form-success" className="card p-8 sm:p-10 text-center flex flex-col items-center">
        <CheckCircle2 size={54} className="text-[#00D9C0] mx-auto mb-5" />
        <h3 className="text-2xl mb-3 text-[#F5F5FA]">Request Submitted</h3>
        <p className="text-[#A0A3C2] mb-8">
          Thank you, {form.name.split(" ")[0]}. Our team is reviewing your project details and will get back to you via email.
        </p>
        
        <div className="bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-xl p-6 w-full text-left">
          <h4 className="text-[#F5F5FA] font-semibold text-[1.05rem] mb-2 flex items-center gap-2">
            <span className="text-[#00D9C0]">Note:</span> Need to start immediately?
          </h4>
          <p className="text-[#A0A3C2] text-sm mb-5 leading-relaxed">
            Standard email evaluations take up to 24 hours. If you are on a tight deadline and want instant support, skip the queue and connect with us on WhatsApp.
          </p>
          <a 
            href="https://wa.me/923119109995" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn btn-wa w-full flex justify-center items-center gap-2 no-underline"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
            Chat with us on WhatsApp
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="quote-form" className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div>
        <label htmlFor="q-name" className="block text-sm text-[#A0A3C2] mb-1.5">Name</label>
        <input id="q-name" data-testid="quote-name-input" className={`field ${errors.name ? "field-error" : ""}`} placeholder="Your name" value={form.name} onChange={set("name")} />
        {errors.name && <p className="text-[#ff6b81] text-xs mt-1.5 m-0">{errors.name}</p>}
      </div>
      <div>
        <label htmlFor="q-email" className="block text-sm text-[#A0A3C2] mb-1.5">Email</label>
        <input id="q-email" type="email" data-testid="quote-email-input" className={`field ${errors.email ? "field-error" : ""}`} placeholder="you@company.com" value={form.email} onChange={set("email")} />
        {errors.email && <p className="text-[#ff6b81] text-xs mt-1.5 m-0">{errors.email}</p>}
      </div>
      <div>
        <label htmlFor="q-service" className="block text-sm text-[#A0A3C2] mb-1.5">Service needed</label>
        <select id="q-service" data-testid="quote-service-select" className={`field ${errors.service ? "field-error" : ""}`} value={form.service} onChange={set("service")}>
          <option value="">Select a service…</option>
          {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {errors.service && <p className="text-[#ff6b81] text-xs mt-1.5 m-0">{errors.service}</p>}
      </div>
      <div>
        <label htmlFor="q-budget" className="block text-sm text-[#A0A3C2] mb-1.5">Plan</label>
        <select id="q-budget" data-testid="quote-budget-select" className={`field ${errors.budget ? "field-error" : ""}`} value={form.budget} onChange={set("budget")}>
          <option value="">Select a plan…</option>
          {availablePlans.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        {errors.budget && <p className="text-[#ff6b81] text-xs mt-1.5 m-0">{errors.budget}</p>}
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="q-details" className="block text-sm text-[#A0A3C2] mb-1.5">Project details</label>
        <textarea id="q-details" rows={5} data-testid="quote-details-input" className={`field resize-y ${errors.details ? "field-error" : ""}`} placeholder="What are you building? Goals, references, anything helpful…" value={form.details} onChange={set("details")} />
        {errors.details && <p className="text-[#ff6b81] text-xs mt-1.5 m-0">{errors.details}</p>}
      </div>
      <div className="sm:col-span-2 flex flex-col sm:flex-row items-start gap-4">
        <button type="submit" data-testid="quote-submit-btn" className="btn btn-primary" disabled={status === "loading"}>
          {status === "loading" ? <><Loader2 size={17} className="animate-spin" /> Sending…</> : <><Send size={16} /> Request a quote</>}
        </button>
        {status === "error" && <p className="text-[#ff6b81] text-sm m-0 self-center">Something went wrong — please try again or message us on WhatsApp.</p>}
      </div>
    </form>
  );
};